import React from 'react';

type TypographyVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'body' | 'body-sm' | 'caption' | 'label';
type TypographyColor = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'inherit' | 'muted';
type TypographyFont = 'brand' | 'display';

interface TypographyProps {
    variant?: TypographyVariant;
    color?: TypographyColor;
    font?: TypographyFont;
    children: React.ReactNode;
    className?: string;
    as?: React.ElementType;
    weight?: 'normal' | 'medium' | 'semibold' | 'bold' | 'black';
}

const variantClasses: Record<TypographyVariant, string> = {
    h1: 'text-3xl md:text-4xl leading-tight',
    h2: 'text-2xl md:text-3xl leading-tight',
    h3: 'text-xl md:text-2xl leading-snug',
    h4: 'text-lg md:text-xl leading-snug',
    body: 'text-base leading-relaxed',
    'body-sm': 'text-sm leading-relaxed',
    caption: 'text-xs leading-normal',
    label: 'text-sm font-medium leading-none'
};

const colorClasses: Record<TypographyColor, string> = {
    primary: 'text-primary',
    secondary: 'text-text-light dark:text-text-dark',
    success: 'text-green-600 dark:text-green-400',
    danger: 'text-red-600 dark:text-red-400',
    warning: 'text-yellow-600 dark:text-yellow-400',
    muted: 'text-gray-500 dark:text-gray-400',
    inherit: 'text-inherit'
};

const weightClasses = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
    black: 'font-black'
};

const defaultElementMap: Record<TypographyVariant, React.ElementType> = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    body: 'p',
    'body-sm': 'p',
    caption: 'span',
    label: 'label'
};

export const Typography: React.FC<TypographyProps> = ({
    variant = 'body',
    color = 'secondary',
    font = 'display',
    weight,
    children,
    className = '',
    as
}) => {
    const Component = as || defaultElementMap[variant];
    
    // Default weights if not specified
    const defaultWeight = weight || (variant.startsWith('h') ? 'bold' : 'normal');

    return (
        <Component className={`
            ${variantClasses[variant]}
            ${colorClasses[color]}
            ${font === 'brand' ? 'font-brand' : 'font-display'}
            ${weightClasses[defaultWeight]}
            ${className}
        `}>
            {children}
        </Component>
    );
};














